import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { Globe, KeyRound, Monitor, Puzzle, Terminal } from "lucide-react";
import type { RememberedDevice } from "../../api/deviceKeys.js";
import type { ClientType } from "../../api/sessions.js";
import { formatDateTime, formatRelativeTime } from "../../lib/format.js";
import { Badge, Button } from "../../ui/primitives.js";

const CLIENT: Record<ClientType, { readonly icon: LucideIcon; readonly label: string }> = {
  web: { icon: Globe, label: "Web-App" },
  desktop: { icon: Monitor, label: "Desktop-App" },
  extension: { icon: Puzzle, label: "Browser-Erweiterung" },
  other: { icon: Terminal, label: "Andere Anwendung" }
};

function rememberLine(device: RememberedDevice, now: number): string {
  if (device.rememberUntil === null || device.rememberUntil <= now) {
    return "zweiter Faktor wieder nötig";
  }
  return `ohne zweiten Faktor bis ${formatDateTime(device.rememberUntil)}`;
}

/** Remembered installations; each can be forgotten (key revoked, signed out). */
export function RememberedDeviceList(props: {
  readonly devices: ReadonlyArray<RememberedDevice>;
  /** Id of the key being revoked right now. */
  readonly forgetting: string | undefined;
  readonly onForget: (device: RememberedDevice) => void;
  readonly empty: string;
}): ReactNode {
  const now = Date.now();
  if (props.devices.length === 0) {
    return <p className="rounded-lg border border-dashed border-line-strong px-5 py-6 text-center text-sm text-mist">{props.empty}</p>;
  }
  return (
    <ul className="divide-y divide-line overflow-hidden rounded-lg border border-line bg-ink-2">
      {props.devices.map((device: RememberedDevice) => {
        const client = CLIENT[device.clientType];
        const Icon = client.icon;
        return (
          <li key={device.id} className="flex items-center gap-3 px-4 py-3">
            <span className="grid size-8 shrink-0 place-items-center rounded-lg border border-line bg-ink-3 text-flow" title={client.label}>
              <Icon size={15} aria-hidden="true" />
              <span className="sr-only">{client.label}</span>
            </span>
            <div className="min-w-0 flex-1">
              <p className="flex items-center gap-2 text-sm font-medium text-foam">
                <span className="truncate">{device.deviceName}</span>
                {device.current ? <Badge tone="mint">Dieses Gerät</Badge> : null}
              </p>
              <p className="text-[0.8125rem] leading-relaxed text-mist-2">
                <span>{rememberLine(device, now)}</span>
                {" · "}
                <span title={device.lastUsedAt === null ? undefined : formatDateTime(device.lastUsedAt)}>
                  {device.lastUsedAt === null ? "noch nicht genutzt" : `zuletzt genutzt ${formatRelativeTime(device.lastUsedAt, now)}`}
                </span>
                {" · "}
                <span>gemerkt seit {formatDateTime(device.createdAt)}</span>
              </p>
            </div>
            <Button
              size="sm"
              variant="secondary"
              icon={KeyRound}
              loading={props.forgetting === device.id}
              disabled={props.forgetting !== undefined && props.forgetting !== device.id}
              aria-label={`${device.deviceName} vergessen`}
              onClick={(): void => props.onForget(device)}
            >
              Vergessen
            </Button>
          </li>
        );
      })}
    </ul>
  );
}
